/**
 * <AssignPoliciesToEmployee /> — manager assigns one or more *individual*
 * policy acknowledgements to one or more existing employees (active or
 * mid-onboarding), without applying a whole onboarding template.
 *
 * Route: /admin/onboarding/assign-policies
 *
 * Use case: a refreshed handbook or a single new policy needs to be signed by
 * people already on the team. Unlike "Assign onboarding" (which provisions a
 * full template and flips the employee to 'onboarding'), this is a light-touch
 * refresh — each employee's status is left untouched. The selected policies
 * show up on every selected employee's /onboarding checklist for signature and
 * they're each emailed a link.
 *
 * Flow:
 *   1. Manager searches and checks any number of employees.
 *   2. Manager checks one or more policies and assigns.
 *   3. We call assignPoliciesToEmployee once per selected employee. The server
 *      skips any policy already on that employee's file, so duplicates are
 *      harmless. We then redirect — to the single employee's view when only one
 *      was picked, otherwise back to the onboarding overview.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileSignature } from 'lucide-react';
import { useApp } from '../../../context/AppContext';
import { useToast } from '../../../components/Toast';
import {
  assignPoliciesToEmployee,
  listActivePolicyDocuments,
} from '../services/onboardingService';
import '../Onboarding.css';

export default function AssignPoliciesToEmployee() {
  const navigate = useNavigate();
  const toast = useToast();
  const { state } = useApp();
  const { employees } = state;

  const [policies, setPolicies] = useState([]);
  const [search, setSearch] = useState('');
  const [employeeIds, setEmployeeIds] = useState([]);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await listActivePolicyDocuments();
        setPolicies(list.sort((a, b) => (a.title || a.id).localeCompare(b.title || b.id)));
      } catch (e) {
        // Policies are optional to preload; the list just renders empty.
      }
    })();
  }, []);

  const eligibleEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (employees || [])
      .filter((e) => {
        if (!q) return true;
        const hay = `${e.firstName || ''} ${e.lastName || ''} ${e.email || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
  }, [employees, search]);

  const selectedEmployees = useMemo(
    () => (employees || []).filter((e) => employeeIds.includes(e.id)),
    [employees, employeeIds]
  );

  const toggleEmployee = (id) => {
    setEmployeeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setErrors((e) => ({ ...e, employeeIds: null }));
  };

  // "Select all" acts on the currently filtered list so a search can narrow the
  // batch before bulk-selecting; toggles to "Clear" once they're all picked.
  const allFilteredSelected =
    eligibleEmployees.length > 0 &&
    eligibleEmployees.every((e) => employeeIds.includes(e.id));

  const toggleSelectAllFiltered = () => {
    setEmployeeIds((prev) => {
      if (allFilteredSelected) {
        const filteredIds = new Set(eligibleEmployees.map((e) => e.id));
        return prev.filter((id) => !filteredIds.has(id));
      }
      const merged = new Set(prev);
      eligibleEmployees.forEach((e) => merged.add(e.id));
      return Array.from(merged);
    });
    setErrors((e) => ({ ...e, employeeIds: null }));
  };

  const togglePolicy = (id) => {
    setSelectedPolicyIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
    setErrors((e) => ({ ...e, policies: null }));
  };

  const validate = () => {
    const errs = {};
    if (employeeIds.length === 0) errs.employeeIds = 'Pick at least one employee';
    if (selectedPolicyIds.length === 0) errs.policies = 'Pick at least one policy';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      // One request per employee. The endpoint is per-employee (it creates that
      // person's checklist items, recalcs their progress, and emails them), so
      // we fan out and aggregate the results.
      const results = await Promise.allSettled(
        employeeIds.map((id) =>
          assignPoliciesToEmployee({ employeeId: id, policyIds: selectedPolicyIds })
        )
      );

      let createdCount = 0;
      let skippedCount = 0;
      let failedCount = 0;
      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          createdCount += r.value?.created?.length ?? 0;
          skippedCount += r.value?.skipped?.length ?? 0;
        } else {
          failedCount += 1;
        }
      });

      const employeeCount = employeeIds.length;
      const employeeLabel =
        employeeCount === 1
          ? selectedEmployees[0]?.firstName || 'employee'
          : `${employeeCount} employees`;

      if (createdCount === 0 && failedCount > 0) {
        toast.error(
          `Failed to assign policies to ${failedCount} employee${failedCount === 1 ? '' : 's'}`
        );
      } else if (createdCount > 0) {
        toast.success(
          `Assigned ${createdCount} polic${createdCount === 1 ? 'y' : 'ies'} across ${employeeLabel}` +
          (skippedCount ? ` (${skippedCount} already on file)` : '') +
          (failedCount ? ` — ${failedCount} failed` : '')
        );
      } else {
        toast.success('No new policies to assign — everything selected was already on file');
      }

      // One employee → jump straight to their tracking view; otherwise back to
      // the onboarding overview where all of them are listed.
      if (employeeCount === 1 && failedCount === 0) {
        navigate(`/admin/onboarding/${employeeIds[0]}`);
      } else {
        navigate('/admin/onboarding');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to assign policies');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="onboarding-shell">
      <h1 style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Assign policies</h1>
      <p style={{ color: '#555' }}>
        Send one or more individual policy acknowledgements to one or more existing employees — for
        example a refreshed handbook or a single new policy. They'll appear on each employee's
        onboarding checklist for signature, and every selected employee gets an email with a link.
        Their status is left unchanged. To assign a full onboarding template instead, use{' '}
        <a href="/admin/onboarding/assign">Assign onboarding</a>.
      </p>

      <form onSubmit={onSubmit}>
        <div className="onboarding-form-row">
          <label>Find employees</label>
          <input
            type="text"
            placeholder="Search by name or email"
            value={search}
            onChange={(ev) => setSearch(ev.target.value)}
          />
        </div>

        <div className="onboarding-form-row">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <label>
              Employees{employeeIds.length ? ` (${employeeIds.length} selected)` : ''}
            </label>
            {eligibleEmployees.length > 0 && (
              <button
                type="button"
                onClick={toggleSelectAllFiltered}
                disabled={submitting}
                style={{
                  background: 'none', border: 'none', color: '#2f6f4f',
                  cursor: 'pointer', fontSize: '0.85rem', padding: 0,
                }}
              >
                {allFilteredSelected ? 'Clear' : 'Select all'}
              </button>
            )}
          </div>
          {eligibleEmployees.length === 0 ? (
            <div style={{ color: '#777', fontSize: '0.85rem', marginTop: 4 }}>
              {employees.length === 0
                ? 'No employees yet. Add one from the Employees page.'
                : 'No employees match your search.'}
            </div>
          ) : (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 6,
              maxHeight: 240, overflowY: 'auto',
              border: '1px solid #e6e3d6', borderRadius: 6, padding: 10,
            }}>
              {eligibleEmployees.map((e) => (
                <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}>
                  <input
                    type="checkbox"
                    checked={employeeIds.includes(e.id)}
                    onChange={() => toggleEmployee(e.id)}
                    disabled={submitting}
                  />
                  {e.firstName} {e.lastName}
                  {e.email ? (
                    <span style={{ color: '#999', fontSize: '0.8rem' }}>({e.email})</span>
                  ) : null}
                  {e.status === 'onboarding' ? (
                    <span style={{ color: '#999', fontSize: '0.8rem' }}>— onboarding</span>
                  ) : null}
                </label>
              ))}
            </div>
          )}
          {errors.employeeIds && <div className="field-error">{errors.employeeIds}</div>}
        </div>

        {employeeIds.length > 0 && (
          <div className="onboarding-form-row">
            <label>Policies</label>
            {policies.length === 0 ? (
              <div style={{ color: '#777', fontSize: '0.85rem' }}>
                No active policies found. Add one under{' '}
                <a href="/admin/policy-templates">Manage templates</a>.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {policies.map((p) => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedPolicyIds.includes(p.id)}
                      onChange={() => togglePolicy(p.id)}
                      disabled={submitting}
                    />
                    {p.title || p.id}
                    {p.current_version ? (
                      <span style={{ color: '#999', fontSize: '0.8rem' }}>v{p.current_version}</span>
                    ) : null}
                  </label>
                ))}
              </div>
            )}
            {errors.policies && <div className="field-error">{errors.policies}</div>}
          </div>
        )}

        {selectedEmployees.length > 0 && (
          <div style={{
            background: '#f6f6f1', border: '1px solid #e6e3d6', borderRadius: 6,
            padding: 10, fontSize: '0.85rem', color: '#555', marginBottom: 8,
          }}>
            Sending {selectedPolicyIds.length || 'these'}{' '}
            {selectedPolicyIds.length === 1 ? 'policy' : 'policies'} to{' '}
            <strong>
              {selectedEmployees.length === 1
                ? `${selectedEmployees[0].firstName} ${selectedEmployees[0].lastName}`
                : `${selectedEmployees.length} employees`}
            </strong>{' '}
            for signature. Any policy already on an employee's file is skipped automatically, and
            their statuses stay unchanged.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            type="button"
            className="onboarding-btn secondary"
            onClick={() => navigate('/admin/onboarding')}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="onboarding-btn"
            disabled={submitting || employeeIds.length === 0 || selectedPolicyIds.length === 0}
            style={{ flex: 1 }}
          >
            <FileSignature size={14} style={{ marginRight: 4 }} />
            {submitting
              ? 'Assigning…'
              : `Assign ${selectedPolicyIds.length || ''} ${selectedPolicyIds.length === 1 ? 'policy' : 'policies'}`.trim()}
          </button>
        </div>
      </form>
    </div>
  );
}
